import { Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
} from "@mui/material";
import { css, StyleSheet } from "aphrodite";
import {
  formatContaContabil,
  formatMulta,
  formatNumber,
  formatValor,
  parseBRLToNumberSigned,
} from "../../helpers/formaters";
import { useEffect } from "react";
import FormasPagamentoTable from "../tables/FormasPagamentoTable";
import ParcelasTable, { parseBRLToNumber } from "../tables/ParcelasTable";

export const FilterContasAPagarLanc = (props) => {
  useEffect(() => {
    const converterParaNumero = (valor) => {
      if (!valor || typeof valor !== "string") return 0;

      const valorLimpo = valor
        .replace("R$", "")
        .replace(/\./g, "")
        .replace(",", ".")
        .trim();

      return parseFloat(valorLimpo) || 0;
    };

    const n1 = converterParaNumero(props.acerto);
    const n2 = converterParaNumero(props.valorValores);

    const soma = n1 + n2;

    const resultadoFormatado = soma.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    props.setValorCorrigido(formatNumber(resultadoFormatado));
  }, [props.acerto, props.valorValores]);

  useEffect(() => {
    const valorBase = clamp0(parseBRLToNumber(props.valorValores));
    const jurosDia = clamp0(parseBRLToNumber(props.valorJurosDia));
    const atrasoDias = clamp0(Number(props.atraso || 0));

    const multaPerc = clamp0(parsePercentBR(props.multa));
    const multaValor = valorBase * (multaPerc / 100);

    const acerto = parseBRLToNumberSigned(props.acerto);

    const valorCorrigidoNovo =
      valorBase + jurosDia * atrasoDias + multaValor + acerto;

    props.setValorCorrigido(toBRL(valorCorrigidoNovo));
  }, [
    props.valorValores,
    props.valorJurosDia,
    props.atraso,
    props.multa,
    props.acerto,
  ]);

  const clamp0 = (n) => Math.max(0, Number(n) || 0);

  const sanitizeSignedInput = (v) => {
    let s = String(v ?? "");
    s = s.replace(/[^\d,.\-]/g, "");
    s = s.replace(/(?!^)-/g, "");
    return s;
  };

  const formatSignedBR = (value) => {
    const n = parseBRLToNumberSigned(value);
    const abs = Math.abs(n).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return n < 0 ? `-${abs}` : abs;
  };

  const parsePercentBR = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const s = String(v)
      .replace("%", "")
      .trim()
      .replace(/\./g, "")
      .replace(",", ".");
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  const toBRL = (n) =>
    Number(n || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const recalcValorCorrigido = (overrides = {}) => {
    const valorBase = clamp0(
      parseBRLToNumber(overrides.valorValores ?? props.valorValores),
    );

    const jurosDia = clamp0(
      parseBRLToNumber(overrides.valorJurosDia ?? props.valorJurosDia),
    );
    const atrasoDias = clamp0(Number(overrides.atraso ?? props.atraso));

    const multaPerc = clamp0(parsePercentBR(overrides.multa ?? props.multa)); // %
    const multaValor = valorBase * (multaPerc / 100);

    const acerto = parseBRLToNumberSigned(overrides.acerto ?? props.acerto);

    const valorCorrigidoNovo =
      valorBase + jurosDia * atrasoDias + multaValor + acerto;

    props.setValorCorrigido(toBRL(valorCorrigidoNovo));
  };

  const handleBlurGerar = (valorManual = null) => {
    if (props.parcela && props.parcela > 0) {
      props.gerarParcelas(props.parcela, valorManual);
    }
  };

  const getBgBySituacao = () => {
    const id = Number(props.situacaoId);

    if (id === 4) return "#E0E0E0";
    if (id === 1) return "#E8F5E9";
    if (id === 3) return "#ffafafff";
    return "#FFF8E1";
  };

  const textFieldStyles = {
    marginTop: "0.5rem",
    "& .MuiInputBase-root": {
      height: "30px",
      fontSize: 12,
      backgroundColor: "inherit",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiInputBase-input": {
      height: "30px",
      boxSizing: "border-box",
      cursor: "text",
      fontSize: "12px",
      textAlign: "left",
      '&[type="date"]': {
        fontSize: "14px",
      },
      "&.Mui-readOnly": {
        fontSize: "12px",
        "-webkit-text-fill-color": "inherit",
      },
      "&::placeholder": {
        fontSize: "12px",
        opacity: 1,
        textAlign: "left",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: 10,
      transform: "translate(10px, 6px) scale(1)",
      "& .MuiFormLabel-asterisk": {
        color: "red",
        fontWeight: "bold",
      },
    },
    "& .MuiInputLabel-shrink": {
      transform: "translate(10px, -8px) scale(0.75)",
    },
  };

  return (
    <>
      <Box>
        <Box>
          {props.isSearch ? (
            <Paper sx={{ borderRadius: 1, overflow: "hidden" }} elevation={1}>
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  bgcolor: "#669966",
                  color: "white",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Consulta de Lançamentos
              </Box>

              <Box sx={{ px: 1, paddingTop: 0.5, paddingBottom: 1.5 }}>
                <Grid container spacing={0.75} alignItems="flex-end">
                  <Grid size={{ xs: 12, sm: 12, md: 12, lg: 8 }}>
                    <TextField
                      sx={textFieldStyles}
                      label="Buscar lançamento"
                      size="small"
                      fullWidth
                      color="success"
                      value={props.searchValue}
                      onChange={(e) => props.setSearchValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") props.searchContasAPagar();
                      }}
                    />
                  </Grid>

                  {/* <Grid size={{ xs: 12, sm: 12, md: 12, lg: 8 }}>
                    <TextField
                      sx={{
                        ...textFieldStyles,
                        "& .MuiSelect-select": {
                          cursor: "pointer !important",
                        },
                      }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              "& .MuiMenuItem-root": {
                                fontSize: "11px",
                                minHeight: "24px",
                                paddingTop: "2px",
                                paddingBottom: "2px",
                              },
                            },
                          },
                        },
                      }}
                      fullWidth
                      select
                      required={false}
                      color="success"
                      label="Fornecedor"
                      size="small"
                      value={props.fornecedor}
                      onChange={(e) => props.setFornecedor(e.target.value)}
                    >
                      <MenuItem key="0" value="0">
                        Todos
                      </MenuItem>

                      {props.fornecedorList &&
                        props.fornecedorList.map((fornecedor) => (
                          <MenuItem key={fornecedor.id} value={fornecedor.id}>
                            {fornecedor.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid> */}

                  <Grid size={{ xs: 12, sm: 6, md: 2.5, lg: 2 }}>
                    <TextField
                      sx={textFieldStyles}
                      label="Data Início"
                      size="small"
                      fullWidth
                      type="date"
                      color="success"
                      InputLabelProps={{ shrink: true }}
                      value={props.dataInicio}
                      onChange={(e) => props.setDataInicio(e.target.value)}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 2.5, lg: 2 }}>
                    <TextField
                      sx={textFieldStyles}
                      label="Data Fim"
                      size="small"
                      fullWidth
                      type="date"
                      color="success"
                      InputLabelProps={{ shrink: true }}
                      value={props.dataFim}
                      onChange={(e) => props.setDataFim(e.target.value)}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 5, lg: 4, xl: 2.5 }}>
                    <Box
                      component="fieldset"
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        px: 1,
                        pt: 0.75,
                        pb: 0.5,
                      }}
                    >
                      <Box
                        component="legend"
                        sx={{ fontSize: 11, color: "text.secondary", px: 0.5 }}
                      >
                        Situação
                      </Box>

                      <FormGroup row sx={{ justifyContent: "space-between" }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              color="success"
                              checked={props.situacaoAberto}
                              onChange={(e) =>
                                props.setSituacaoAberto?.(e.target.checked)
                              }
                            />
                          }
                          label="Aberto"
                          sx={{
                            "& .MuiFormControlLabel-label": { fontSize: 12 },
                          }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              color="success"
                              checked={props.situacaoPago}
                              onChange={(e) =>
                                props.setSituacaoPago?.(e.target.checked)
                              }
                            />
                          }
                          label="Pago"
                          sx={{
                            "& .MuiFormControlLabel-label": { fontSize: 12 },
                          }}
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              color="success"
                              checked={props.situacaoCancelado}
                              onChange={(e) =>
                                props.setSituacaoCancelado?.(e.target.checked)
                              }
                            />
                          }
                          label="Cancelado"
                          sx={{
                            "& .MuiFormControlLabel-label": { fontSize: 12 },
                          }}
                        />
                      </FormGroup>
                    </Box>
                  </Grid>

                  <Grid
                    size={{ xs: 12, sm: 6, md: 2, lg: 1 }}
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "flex-start", sm: "flex-end" },
                    }}
                  >
                    <IconButton
                      onClick={() => props.searchContasAPagar()}
                      className={css(styles.btn)}
                      title="Buscar"
                    >
                      <Search fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          ) : props.isMultiLanc ? (
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12 }}>
                  <Paper
                    sx={{ borderRadius: 1, overflow: "hidden" }}
                    elevation={1}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 0.5,
                        bgcolor: "#669966",
                        color: "white",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      Identificação
                    </Box>

                    <Box sx={{ px: 1, pt: 0.5, pb: 1.5 }}>
                      <Grid container spacing={1} alignItems="flex-end">
                        <Grid size={{ xs: 12, sm: 12, md: 7, lg: 8, xl: 2.4 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            select
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  sx: {
                                    "& .MuiMenuItem-root": {
                                      fontSize: "11px",
                                      minHeight: "24px",
                                      paddingTop: "2px",
                                      paddingBottom: "2px",
                                    },
                                  },
                                },
                              },
                              renderValue: (value) => {
                                const f = (props.fornecedorList || []).find(
                                  (x) => String(x.id) === String(value),
                                );
                                return f ? f.name : "";
                              },
                            }}
                            InputProps={
                              ["1", "4"].includes(String(props.situacaoId))
                                ? {
                                    readOnly: true,
                                    style: {
                                      fontFamily: "Poppins",
                                      fontSize: 11,
                                      color: "#000000ff",
                                      backgroundColor: "#d2d2d2ff",
                                    },
                                  }
                                : {}
                            }
                            disabled={["1", "4"].includes(
                              String(props.situacaoId),
                            )}
                            color="success"
                            label="Fornecedor"
                            required
                            size="small"
                            value={props.fornecedor}
                            onChange={(e) =>
                              props.setFornecedor(e.target.value)
                            }
                          >
                            {(props.fornecedorListAtivos || []).map(
                              (fornecedor) => (
                                <MenuItem
                                  key={fornecedor.id}
                                  value={fornecedor.id}
                                >
                                  {fornecedor.name}
                                </MenuItem>
                              ),
                            )}
                          </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 5, lg: 4, xl: 2.4 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            select
                            InputProps={
                              ["1", "4"].includes(String(props.situacaoId))
                                ? {
                                    readOnly: true,
                                    style: {
                                      fontFamily: "Poppins",
                                      fontSize: 11,
                                      color: "#000000ff",
                                      backgroundColor: "#d2d2d2ff",
                                    },
                                  }
                                : {}
                            }
                            disabled={["1", "4"].includes(
                              String(props.situacaoId),
                            )}
                            required
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  sx: {
                                    "& .MuiMenuItem-root": {
                                      fontSize: "11px",
                                      minHeight: "24px",
                                      paddingTop: "2px",
                                      paddingBottom: "2px",
                                    },
                                  },
                                },
                              },
                              renderValue: (value) => {
                                const f = (props.tipoDocList || []).find(
                                  (x) => String(x.id) === String(value),
                                );
                                return f ? f.name : "";
                              },
                            }}
                            color="success"
                            label="Tipo documento"
                            size="small"
                            value={props.tipoDoc}
                            onChange={(e) => props.setTipoDoc(e.target.value)}
                          >
                            {(props.tipoDocListAtivos || []).map((tipoDoc) => (
                              <MenuItem key={tipoDoc.id} value={tipoDoc.id}>
                                {tipoDoc.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 4.5, lg: 4, xl: 2.4 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            select
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  sx: {
                                    "& .MuiMenuItem-root": {
                                      fontSize: "11px",
                                      minHeight: "24px",
                                      paddingTop: "2px",
                                      paddingBottom: "2px",
                                    },
                                  },
                                },
                              },
                              renderValue: (value) => {
                                const f = (
                                  props.estabelecimentoList || []
                                ).find((x) => String(x.id) === String(value));
                                return f ? f.name : "";
                              },
                            }}
                            required
                            color="success"
                            label="Estabelecimento"
                            size="small"
                            value={props.estabelecimento}
                            onChange={(e) =>
                              props.setEstabelecimento(e.target.value)
                            }
                          >
                            {(props.estabelecimentoListAtivos || []).map(
                              (estabelecimento) => (
                                <MenuItem
                                  key={estabelecimento.id}
                                  value={estabelecimento.id}
                                >
                                  {estabelecimento.name}
                                </MenuItem>
                              ),
                            )}
                          </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 5, md: 3, lg: 4, xl: 2.4 }}>
                          <TextField
                            sx={textFieldStyles}
                            label="Nº Documento"
                            fullWidth
                            value={props.numDoc}
                            onChange={(e) => props.setNumDoc(e.target.value)}
                            size="small"
                            color="success"
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 7, md: 4.5, lg: 4, xl: 2.4 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            select
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  sx: {
                                    "& .MuiMenuItem-root": {
                                      fontSize: "11px",
                                      minHeight: "24px",
                                      paddingTop: "2px",
                                      paddingBottom: "2px",
                                    },
                                  },
                                },
                              },
                              renderValue: (value) => {
                                const f = (props.agenteCobradorList || []).find(
                                  (x) => String(x.id) === String(value),
                                );
                                return f ? f.name : "";
                              },
                            }}
                            InputProps={
                              ["1", "4"].includes(String(props.situacaoId))
                                ? {
                                    readOnly: true,
                                    style: {
                                      fontFamily: "Poppins",
                                      fontSize: 11,
                                      color: "#000000ff",
                                      backgroundColor: "#d2d2d2ff",
                                    },
                                  }
                                : {}
                            }
                            disabled={["1", "4"].includes(
                              String(props.situacaoId),
                            )}
                            color="success"
                            label="Agente Cobrador"
                            size="small"
                            value={props.agenteCobrador}
                            onChange={(e) =>
                              props.setAgenteCobrador(e.target.value)
                            }
                          >
                            {(props.agenteCobradorListAtivos || []).map(
                              (agenteCobrador) => (
                                <MenuItem
                                  key={agenteCobrador.id}
                                  value={agenteCobrador.id}
                                >
                                  {agenteCobrador.name}
                                </MenuItem>
                              ),
                            )}
                          </TextField>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Paper
                    sx={{ borderRadius: 1, overflow: "hidden" }}
                    elevation={1}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 0.5,
                        bgcolor: "#669966",
                        color: "white",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      Classificação
                    </Box>

                    <Box sx={{ px: 1, pt: 0.5, pb: 1.5 }}>
                      <Grid container spacing={1} alignItems="flex-end">
                        <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 3 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            select
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  sx: {
                                    "& .MuiMenuItem-root": {
                                      fontSize: "11px",
                                      minHeight: "24px",
                                      paddingTop: "2px",
                                      paddingBottom: "2px",
                                    },
                                  },
                                },
                              },
                              renderValue: (value) => {
                                const f = (props.centroCustoList || []).find(
                                  (x) => String(x.id) === String(value),
                                );
                                return f ? f.name : "";
                              },
                            }}
                            InputProps={
                              ["1", "4"].includes(String(props.situacaoId))
                                ? {
                                    readOnly: true,
                                    style: {
                                      fontFamily: "Poppins",
                                      fontSize: 11,
                                      color: "#000000ff",
                                      backgroundColor: "#d2d2d2ff",
                                    },
                                  }
                                : {}
                            }
                            disabled={["1", "4"].includes(
                              String(props.situacaoId),
                            )}
                            color="success"
                            label="Centro de Custos"
                            size="small"
                            value={props.centroCusto}
                            required
                            onChange={(e) => props.handleChangeCentroCusto(e)}
                          >
                            {(props.centroCustoListAtivos || []).map(
                              (centroCusto) => (
                                <MenuItem
                                  key={centroCusto.id}
                                  value={centroCusto.id}
                                >
                                  {centroCusto.name}
                                </MenuItem>
                              ),
                            )}
                          </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 3 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            select
                            required
                            InputProps={
                              ["1", "4"].includes(String(props.situacaoId))
                                ? {
                                    readOnly: true,
                                    style: {
                                      fontFamily: "Poppins",
                                      fontSize: 11,
                                      color: "#000000ff",
                                      backgroundColor: "#d2d2d2ff",
                                    },
                                  }
                                : {}
                            }
                            disabled={["1", "4"].includes(
                              String(props.situacaoId),
                            )}
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  sx: {
                                    "& .MuiMenuItem-root": {
                                      fontSize: "11px",
                                      minHeight: "24px",
                                      paddingTop: "2px",
                                      paddingBottom: "2px",
                                    },
                                  },
                                },
                              },
                              renderValue: (value) => {
                                const f = (props.despesaList || []).find(
                                  (x) => String(x.id) === String(value),
                                );
                                return f ? f.name : "";
                              },
                            }}
                            color="success"
                            label="Despesa"
                            size="small"
                            value={props.despesa}
                            onChange={(e) => props.handleChangeDespesa(e)}
                          >
                            {(props.despesaListAtivos || []).map((despesa) => (
                              <MenuItem key={despesa.id} value={despesa.id}>
                                {despesa.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 3 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            required
                            select
                            color="success"
                            label="Conta Contábil"
                            size="small"
                            SelectProps={{
                              MenuProps: {
                                PaperProps: {
                                  sx: {
                                    "& .MuiMenuItem-root": {
                                      fontSize: "11px",
                                      minHeight: "24px",
                                      paddingTop: "2px",
                                      paddingBottom: "2px",
                                    },
                                  },
                                },
                              },
                              renderValue: (value) => {
                                const f = (props.planoContasList || []).find(
                                  (x) => String(x.id) === String(value),
                                );
                                return f ? f.name : "";
                              },
                            }}
                            value={props.contaContabil}
                            onChange={(e) =>
                              props.setContaContabil(e.target.value)
                            }
                          >
                            {(props.planoContasListAtivos || []).map(
                              (planoContas) => (
                                <MenuItem
                                  key={planoContas.id}
                                  value={planoContas.id}
                                  disabled={!planoContas.analitica}
                                  sx={{
                                    fontWeight: planoContas.analitica
                                      ? "normal"
                                      : "bold",
                                    backgroundColor: planoContas.analitica
                                      ? "transparent"
                                      : "#f5f5f5",
                                  }}
                                >
                                  {planoContas.id} - {planoContas.name}
                                </MenuItem>
                              ),
                            )}
                          </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 3 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            required
                            color="success"
                            label="Breve Descrição"
                            size="small"
                            value={props.breveDescricao}
                            onChange={(e) =>
                              props.setBreveDescricao(e.target.value)
                            }
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Paper
                    sx={{
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                    elevation={1}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 0.5,
                        bgcolor: "#669966",
                        color: "white",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      Múltiplas Parcelas
                    </Box>
                    <Box sx={{ px: 1, pt: 1.5, pb: 1.5 }}>
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 12, sm: 12, md: 4, lg: 3, xl: 2.75 }}>
                          <FormControl
                            component="fieldset"
                            variant="outlined"
                            fullWidth
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              px: 2,
                              pt: 1,
                              pb: 0.5,
                              position: "relative",
                            }}
                          >
                            <FormLabel
                              component="legend"
                              color="success"
                              sx={{
                                position: "absolute",
                                top: -10,
                                left: 12,
                                px: 1,
                                bgcolor: "background.paper",
                                fontSize: 11,
                                color: "text.secondary",
                              }}
                            >
                              Antecipa vencto.
                            </FormLabel>
                            <Box sx={{ display: "flex" }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    color="success"
                                    checked={props.antecipaFeriado}
                                    onChange={(e) =>
                                      props.setAntecipaFeriado(e.target.checked)
                                    }
                                  />
                                }
                                label={
                                  <span style={{ fontSize: "11px" }}>
                                    Feriado
                                  </span>
                                }
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    color="success"
                                    checked={props.antecipaFimSemana}
                                    onChange={(e) =>
                                      props.setAntecipaFimSemana(
                                        e.target.checked,
                                      )
                                    }
                                  />
                                }
                                label={
                                  <span style={{ fontSize: "11px" }}>
                                    Fim de semana
                                  </span>
                                }
                              />
                            </Box>
                          </FormControl>
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            sm: 12,
                            md: 8,
                            lg: 9,
                            xl: 9.25,
                          }}
                        >
                          <Grid container spacing={1} alignItems="flex-end">
                            <Grid
                              size={{
                                xs: 12,
                                sm: 6,
                                md: 6,
                                lg: 3,
                                xl: 2,
                              }}
                            >
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                type="date"
                                size="small"
                                required
                                color="success"
                                label="Emissão"
                                value={props.emissao}
                                onChange={(e) =>
                                  props.setEmissao(e.target.value)
                                }
                                onBlur={() => handleBlurGerar()}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid
                              size={{
                                xs: 12,
                                sm: 6,
                                md: 6,
                                lg: 3,
                                xl: 2,
                              }}
                            >
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                type="date"
                                size="small"
                                required
                                color="success"
                                label="Vencimento Inicial"
                                value={props.venctoInicial}
                                onChange={(e) =>
                                  props.setVenctoInicial(e.target.value)
                                }
                                onBlur={() => handleBlurGerar()}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid
                              size={{
                                xs: 6,
                                sm: 6,
                                md: 3,
                                lg: 3,
                                xl: 2,
                              }}
                            >
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                label="Valor"
                                required
                                size="small"
                                color="success"
                                value={props.valorMulti}
                                onChange={(e) =>
                                  props.setValorMulti(e.target.value)
                                }
                                onBlur={(e) => {
                                  const valorFormatado = formatValor(
                                    e.target.value,
                                    true,
                                  );
                                  props.setValorMulti(valorFormatado);
                                  handleBlurGerar(valorFormatado);
                                }}
                              />
                            </Grid>
                            <Grid
                              size={{
                                xs: 6,
                                sm: 6,
                                md: 3,
                                lg: 3,
                                xl: 2,
                              }}
                            >
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                size="small"
                                color="success"
                                value={props.prazo}
                                onChange={(e) => props.setPrazo(e.target.value)}
                                label="Prazo"
                                onBlur={() => handleBlurGerar()}
                              />
                            </Grid>
                            <Grid
                              size={{
                                xs: 6,
                                sm: 6,
                                md: 3,
                                lg: 3,
                                xl: 2,
                              }}
                            >
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                label="Dia Específico"
                                size="small"
                                color="success"
                                value={props.diaEspecifico}
                                onChange={(e) =>
                                  props.setDiaEspecifico(e.target.value)
                                }
                                onBlur={() => handleBlurGerar()}
                              />
                            </Grid>
                            <Grid
                              size={{
                                xs: 6,
                                sm: 6,
                                md: 3,
                                lg: 3,
                                xl: 2,
                              }}
                            >
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                label="Parcelas"
                                size="small"
                                required
                                color="success"
                                value={props.parcela}
                                onChange={(e) =>
                                  props.setParcela(e.target.value)
                                }
                                onBlur={(e) =>
                                  props.gerarParcelas(e.target.value)
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>

                <Box
                  sx={{
                    width: "100%",
                    display: "grid",
                  }}
                >
                  <ParcelasTable rows={props.parcelasGeradas} height={200} />
                </Box>

                <Grid
                  size={{ xs: 12 }}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Grid
                    size={{ xs: 12 }}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="inherit"
                      sx={{ minWidth: 120, fontSize: 12 }}
                      onClick={() => props.handleBack()}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => props.cadLancamento()}
                      sx={{
                        minWidth: 120,
                        fontSize: 12,
                        backgroundColor: "#669966",
                      }}
                    >
                      Salvar
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="stretch">
                <Grid size={{ xs: 12, md: 8, lg: 9, xl: 9 }}>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12 }}>
                      <Paper
                        sx={{ borderRadius: 1, overflow: "hidden" }}
                        elevation={1}
                      >
                        <Box
                          sx={{
                            px: 2,
                            py: 0.5,
                            bgcolor: "#669966",
                            color: "white",
                            fontWeight: 600,
                            fontSize: 14,
                          }}
                        >
                          Identificação
                        </Box>

                        <Box sx={{ px: 1, pt: 0.5, pb: 1.5 }}>
                          <Grid container spacing={1} alignItems="flex-end">
                            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                select
                                SelectProps={{
                                  MenuProps: {
                                    PaperProps: {
                                      sx: {
                                        "& .MuiMenuItem-root": {
                                          fontSize: "11px",
                                          minHeight: "24px",
                                          paddingTop: "2px",
                                          paddingBottom: "2px",
                                        },
                                      },
                                    },
                                  },
                                  renderValue: (value) => {
                                    const f = (props.fornecedorList || []).find(
                                      (x) => String(x.id) === String(value),
                                    );
                                    return f ? f.name : "";
                                  },
                                }}
                                InputProps={
                                  ["1", "4"].includes(String(props.situacaoId))
                                    ? {
                                        readOnly: true,
                                        style: {
                                          fontFamily: "Poppins",
                                          fontSize: 11,
                                          color: "#000000ff",
                                          backgroundColor: "#d2d2d2ff",
                                        },
                                      }
                                    : {}
                                }
                                disabled={["1", "4"].includes(
                                  String(props.situacaoId),
                                )}
                                color="success"
                                label="Fornecedor"
                                required
                                size="small"
                                value={props.fornecedor}
                                onChange={(e) =>
                                  props.setFornecedor(e.target.value)
                                }
                              >
                                {(props.fornecedorListAtivos || []).map(
                                  (fornecedor) => (
                                    <MenuItem
                                      key={fornecedor.id}
                                      value={fornecedor.id}
                                    >
                                      {fornecedor.name}
                                    </MenuItem>
                                  ),
                                )}
                              </TextField>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                select
                                InputProps={
                                  ["1", "4"].includes(String(props.situacaoId))
                                    ? {
                                        readOnly: true,
                                        style: {
                                          fontFamily: "Poppins",
                                          fontSize: 11,
                                          color: "#000000ff",
                                          backgroundColor: "#d2d2d2ff",
                                        },
                                      }
                                    : {}
                                }
                                disabled={["1", "4"].includes(
                                  String(props.situacaoId),
                                )}
                                required
                                SelectProps={{
                                  MenuProps: {
                                    PaperProps: {
                                      sx: {
                                        "& .MuiMenuItem-root": {
                                          fontSize: "11px",
                                          minHeight: "24px",
                                          paddingTop: "2px",
                                          paddingBottom: "2px",
                                        },
                                      },
                                    },
                                  },
                                  renderValue: (value) => {
                                    const f = (props.tipoDocList || []).find(
                                      (x) => String(x.id) === String(value),
                                    );
                                    return f ? f.name : "";
                                  },
                                }}
                                color="success"
                                label="Tipo documento"
                                size="small"
                                value={props.tipoDoc}
                                onChange={(e) =>
                                  props.setTipoDoc(e.target.value)
                                }
                              >
                                {(props.tipoDocListAtivos || []).map(
                                  (tipoDoc) => (
                                    <MenuItem
                                      key={tipoDoc.id}
                                      value={tipoDoc.id}
                                    >
                                      {tipoDoc.name}
                                    </MenuItem>
                                  ),
                                )}
                              </TextField>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                select
                                SelectProps={{
                                  MenuProps: {
                                    PaperProps: {
                                      sx: {
                                        "& .MuiMenuItem-root": {
                                          fontSize: "11px",
                                          minHeight: "24px",
                                          paddingTop: "2px",
                                          paddingBottom: "2px",
                                        },
                                      },
                                    },
                                  },
                                  renderValue: (value) => {
                                    const f = (
                                      props.estabelecimentoList || []
                                    ).find(
                                      (x) => String(x.id) === String(value),
                                    );
                                    return f ? f.name : "";
                                  },
                                }}
                                required
                                color="success"
                                label="Estabelecimento"
                                size="small"
                                value={props.estabelecimento}
                                onChange={(e) =>
                                  props.setEstabelecimento(e.target.value)
                                }
                              >
                                {(props.estabelecimentoListAtivos || []).map(
                                  (estabelecimento) => (
                                    <MenuItem
                                      key={estabelecimento.id}
                                      value={estabelecimento.id}
                                    >
                                      {estabelecimento.name}
                                    </MenuItem>
                                  ),
                                )}
                              </TextField>
                            </Grid>
                          </Grid>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Paper
                        sx={{ borderRadius: 1, overflow: "hidden" }}
                        elevation={1}
                      >
                        <Box
                          sx={{
                            px: 2,
                            py: 0.5,
                            bgcolor: "#669966",
                            color: "white",
                            fontWeight: 600,
                            fontSize: 14,
                          }}
                        >
                          Documento
                        </Box>

                        <Box sx={{ px: 1, pt: 0.5, pb: 1.5 }}>
                          <Grid container spacing={1} alignItems="flex-end">
                            <Grid size={{ xs: 12, sm: 6, md: 2.5, lg: 2.5 }}>
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                value={props.numTitulo}
                                label="Nº do título"
                                size="small"
                                color="success"
                                InputProps={{
                                  readOnly: true,
                                  style: {
                                    fontFamily: "Poppins",
                                    fontSize: 11,
                                    color: "#000000ff",
                                    backgroundColor: "#d2d2d2ff",
                                  },
                                }}
                              />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                required
                                value={props.parcela}
                                onChange={(e) =>
                                  props.setParcela(e.target.value)
                                }
                                label="Parc."
                                size="small"
                                InputProps={
                                  ["1", "4"].includes(String(props.situacaoId))
                                    ? {
                                        readOnly: true,
                                        style: {
                                          fontFamily: "Poppins",
                                          fontSize: 11,
                                          color: "#000000ff",
                                          backgroundColor: "#d2d2d2ff",
                                        },
                                      }
                                    : {}
                                }
                                disabled={["1", "4"].includes(
                                  String(props.situacaoId),
                                )}
                                color="success"
                              />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3.5 }}>
                              <TextField
                                sx={textFieldStyles}
                                label="Nº Documento"
                                fullWidth
                                value={props.numDoc}
                                onChange={(e) =>
                                  props.setNumDoc(e.target.value)
                                }
                                size="small"
                                color="success"
                                InputProps={
                                  ["1", "4"].includes(String(props.situacaoId))
                                    ? {
                                        readOnly: true,
                                        style: {
                                          fontFamily: "Poppins",
                                          fontSize: 11,
                                          color: "#000000ff",
                                          backgroundColor: "#d2d2d2ff",
                                        },
                                      }
                                    : {}
                                }
                                disabled={["1", "4"].includes(
                                  String(props.situacaoId),
                                )}
                              />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 3.5, lg: 4 }}>
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                select
                                SelectProps={{
                                  MenuProps: {
                                    PaperProps: {
                                      sx: {
                                        "& .MuiMenuItem-root": {
                                          fontSize: "11px",
                                          minHeight: "24px",
                                          paddingTop: "2px",
                                          paddingBottom: "2px",
                                        },
                                      },
                                    },
                                  },
                                  renderValue: (value) => {
                                    const f = (
                                      props.agenteCobradorList || []
                                    ).find(
                                      (x) => String(x.id) === String(value),
                                    );
                                    return f ? f.name : "";
                                  },
                                }}
                                InputProps={
                                  ["1", "4"].includes(String(props.situacaoId))
                                    ? {
                                        readOnly: true,
                                        style: {
                                          fontFamily: "Poppins",
                                          fontSize: 11,
                                          color: "#000000ff",
                                          backgroundColor: "#d2d2d2ff",
                                        },
                                      }
                                    : {}
                                }
                                disabled={["1", "4"].includes(
                                  String(props.situacaoId),
                                )}
                                color="success"
                                label="Agente Cobrador"
                                size="small"
                                value={props.agenteCobrador}
                                onChange={(e) =>
                                  props.setAgenteCobrador(e.target.value)
                                }
                              >
                                {(props.agenteCobradorListAtivos || []).map(
                                  (agenteCobrador) => (
                                    <MenuItem
                                      key={agenteCobrador.id}
                                      value={agenteCobrador.id}
                                    >
                                      {agenteCobrador.name}
                                    </MenuItem>
                                  ),
                                )}
                              </TextField>
                            </Grid>
                          </Grid>
                        </Box>
                      </Paper>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Paper
                        sx={{ borderRadius: 1, overflow: "hidden" }}
                        elevation={1}
                      >
                        <Box
                          sx={{
                            px: 2,
                            py: 0.5,
                            bgcolor: "#669966",
                            color: "white",
                            fontWeight: 600,
                            fontSize: 14,
                          }}
                        >
                          Classificação
                        </Box>

                        <Box sx={{ px: 1, pt: 0.5, pb: 1.5 }}>
                          <Grid container spacing={1} alignItems="flex-end">
                            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 3 }}>
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                select
                                SelectProps={{
                                  MenuProps: {
                                    PaperProps: {
                                      sx: {
                                        "& .MuiMenuItem-root": {
                                          fontSize: "11px",
                                          minHeight: "24px",
                                          paddingTop: "2px",
                                          paddingBottom: "2px",
                                        },
                                      },
                                    },
                                  },
                                  renderValue: (value) => {
                                    const f = (
                                      props.centroCustoList || []
                                    ).find(
                                      (x) => String(x.id) === String(value),
                                    );
                                    return f ? f.name : "";
                                  },
                                }}
                                InputProps={
                                  ["1", "4"].includes(String(props.situacaoId))
                                    ? {
                                        readOnly: true,
                                        style: {
                                          fontFamily: "Poppins",
                                          fontSize: 11,
                                          color: "#000000ff",
                                          backgroundColor: "#d2d2d2ff",
                                        },
                                      }
                                    : {}
                                }
                                disabled={["1", "4"].includes(
                                  String(props.situacaoId),
                                )}
                                color="success"
                                label="Centro de Custos"
                                size="small"
                                value={props.centroCusto}
                                required
                                onChange={(e) =>
                                  props.handleChangeCentroCusto(e)
                                }
                              >
                                {(props.centroCustoListAtivos || []).map(
                                  (centroCusto) => (
                                    <MenuItem
                                      key={centroCusto.id}
                                      value={centroCusto.id}
                                    >
                                      {centroCusto.name}
                                    </MenuItem>
                                  ),
                                )}
                              </TextField>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 3 }}>
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                select
                                required
                                InputProps={
                                  ["1", "4"].includes(String(props.situacaoId))
                                    ? {
                                        readOnly: true,
                                        style: {
                                          fontFamily: "Poppins",
                                          fontSize: 11,
                                          color: "#000000ff",
                                          backgroundColor: "#d2d2d2ff",
                                        },
                                      }
                                    : {}
                                }
                                disabled={["1", "4"].includes(
                                  String(props.situacaoId),
                                )}
                                SelectProps={{
                                  MenuProps: {
                                    PaperProps: {
                                      sx: {
                                        "& .MuiMenuItem-root": {
                                          fontSize: "11px",
                                          minHeight: "24px",
                                          paddingTop: "2px",
                                          paddingBottom: "2px",
                                        },
                                      },
                                    },
                                  },
                                  renderValue: (value) => {
                                    const f = (props.despesaList || []).find(
                                      (x) => String(x.id) === String(value),
                                    );
                                    return f ? f.name : "";
                                  },
                                }}
                                color="success"
                                label="Despesa"
                                size="small"
                                value={props.despesa}
                                onChange={(e) => props.handleChangeDespesa(e)}
                              >
                                {(props.despesaListAtivos || []).map(
                                  (despesa) => (
                                    <MenuItem
                                      key={despesa.id}
                                      value={despesa.id}
                                    >
                                      {despesa.name}
                                    </MenuItem>
                                  ),
                                )}
                              </TextField>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 4, md: 6, lg: 6, xl: 3 }}>
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                required
                                select
                                color="success"
                                label="Conta Contábil"
                                size="small"
                                SelectProps={{
                                  MenuProps: {
                                    PaperProps: {
                                      sx: {
                                        "& .MuiMenuItem-root": {
                                          fontSize: "11px",
                                          minHeight: "24px",
                                          paddingTop: "2px",
                                          paddingBottom: "2px",
                                        },
                                      },
                                    },
                                  },
                                  renderValue: (value) => {
                                    const f = (
                                      props.planoContasList || []
                                    ).find(
                                      (x) => String(x.id) === String(value),
                                    );
                                    return f ? f.name : "";
                                  },
                                }}
                                value={props.contaContabil}
                                onChange={(e) =>
                                  props.setContaContabil(e.target.value)
                                }
                              >
                                {(props.planoContasListAtivos || []).map(
                                  (planoContas) => (
                                    <MenuItem
                                      key={planoContas.id}
                                      value={planoContas.id}
                                      disabled={!planoContas.analitica}
                                      sx={{
                                        fontWeight: planoContas.analitica
                                          ? "normal"
                                          : "bold",
                                        backgroundColor: planoContas.analitica
                                          ? "transparent"
                                          : "#f5f5f5",
                                      }}
                                    >
                                      {planoContas.id} - {planoContas.name}
                                    </MenuItem>
                                  ),
                                )}
                              </TextField>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 8, md: 6, lg: 6, xl: 3 }}>
                              <TextField
                                sx={textFieldStyles}
                                fullWidth
                                required
                                color="success"
                                label="Breve Descrição"
                                size="small"
                                value={props.breveDescricao}
                                onChange={(e) =>
                                  props.setBreveDescricao(e.target.value)
                                }
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Paper>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Paper
                        sx={{ borderRadius: 1, overflow: "hidden" }}
                        elevation={1}
                      >
                        <Box
                          sx={{
                            px: 2,
                            py: 0.5,
                            bgcolor: "#669966",
                            color: "white",
                            fontWeight: 600,
                            fontSize: 14,
                          }}
                        >
                          Datas e Pagamento
                        </Box>

                        <Box sx={{ px: 1, pt: 1.5, pb: 1.5 }}>
                          <Grid container spacing={1.5} alignItems="stretch">
                            <Grid size={{ xs: 12, md: 12, lg: 8, xl: 9 }}>
                              <Box
                                sx={{
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1.5,
                                }}
                              >
                                <TextField
                                  sx={{
                                    ...textFieldStyles,
                                    flex: 1,
                                    "& .MuiInputBase-root": {
                                      height: "100%",
                                      alignItems: "flex-start",
                                    },
                                    "& textarea": {
                                      height: "100% !important",
                                      overflow: "auto",
                                    },
                                  }}
                                  fullWidth
                                  color="success"
                                  label="Histórico"
                                  size="small"
                                  value={props.historico}
                                  onChange={(e) =>
                                    props.setHistorico(e.target.value)
                                  }
                                  multiline
                                  minRows={4}
                                />
                                <FormControl
                                  component="fieldset"
                                  variant="outlined"
                                  fullWidth
                                  sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    px: 2,
                                    pt: 2,
                                    pb: 1.5,
                                    position: "relative",
                                  }}
                                >
                                  <FormLabel
                                    component="legend"
                                    sx={{
                                      position: "absolute",
                                      top: -10,
                                      left: 12,
                                      px: 1,
                                      bgcolor: "background.paper",
                                      fontSize: 11,
                                      color: "text.secondary",
                                    }}
                                  >
                                    Datas do Título
                                  </FormLabel>

                                  <Grid
                                    container
                                    spacing={1}
                                    alignItems="flex-end"
                                  >
                                    <Grid
                                      size={{ xs: 12, sm: 6, md: 6, xl: 3 }}
                                    >
                                      <TextField
                                        sx={textFieldStyles}
                                        fullWidth
                                        type="date"
                                        size="small"
                                        InputProps={
                                          ["1", "4"].includes(
                                            String(props.situacaoId),
                                          )
                                            ? {
                                                readOnly: true,
                                                style: {
                                                  fontFamily: "Poppins",
                                                  fontSize: 11,
                                                  color: "#000000ff",
                                                  backgroundColor: "#d2d2d2ff",
                                                },
                                              }
                                            : {}
                                        }
                                        disabled={["1", "4"].includes(
                                          String(props.situacaoId),
                                        )}
                                        color="success"
                                        label="Data Emissão"
                                        value={props.dataEmissao}
                                        onChange={(e) =>
                                          props.setDataEmissao(e.target.value)
                                        }
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </Grid>

                                    <Grid
                                      size={{ xs: 12, sm: 6, md: 6, xl: 3 }}
                                    >
                                      <TextField
                                        sx={textFieldStyles}
                                        fullWidth
                                        value={props.dataVencto}
                                        required
                                        InputProps={
                                          ["1", "4"].includes(
                                            String(props.situacaoId),
                                          )
                                            ? {
                                                readOnly: true,
                                                style: {
                                                  fontFamily: "Poppins",
                                                  fontSize: 11,
                                                  color: "#000000ff",
                                                  backgroundColor: "#d2d2d2ff",
                                                },
                                              }
                                            : {}
                                        }
                                        disabled={["1", "4"].includes(
                                          String(props.situacaoId),
                                        )}
                                        type="date"
                                        onChange={(ev) => {
                                          const val = ev.target.value;
                                          const [ano, mes, dia] =
                                            val.split("-");
                                          if (ano && ano.length > 4) {
                                            props.setDataVencto(
                                              `${ano.slice(0, 4)}-${
                                                mes || ""
                                              }-${dia || ""}`,
                                            );
                                          } else {
                                            props.setDataVencto(val);
                                          }
                                        }}
                                        size="small"
                                        color="success"
                                        label="Data Vencto."
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ maxLength: 10 }}
                                      />
                                    </Grid>

                                    <Grid
                                      size={{ xs: 12, sm: 6, md: 6, xl: 3 }}
                                    >
                                      <TextField
                                        sx={textFieldStyles}
                                        fullWidth
                                        size="small"
                                        color="success"
                                        type="date"
                                        InputProps={
                                          ["1", "4"].includes(
                                            String(props.situacaoId),
                                          )
                                            ? {
                                                readOnly: true,
                                                style: {
                                                  fontFamily: "Poppins",
                                                  fontSize: 11,
                                                  color: "#000000ff",
                                                  backgroundColor: "#d2d2d2ff",
                                                },
                                              }
                                            : {}
                                        }
                                        disabled={["1", "4"].includes(
                                          String(props.situacaoId),
                                        )}
                                        label="Data Previsão"
                                        value={props.dataPrevisao}
                                        required
                                        onChange={(e) =>
                                          props.setDataPrevisao(e.target.value)
                                        }
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </Grid>

                                    <Grid
                                      size={{ xs: 12, sm: 6, md: 6, xl: 3 }}
                                    >
                                      <TextField
                                        sx={textFieldStyles}
                                        fullWidth
                                        size="small"
                                        color="success"
                                        value={props.atraso}
                                        onChange={(e) =>
                                          props.setAtraso(e.target.value)
                                        }
                                        label="Atraso"
                                        InputProps={{
                                          readOnly: true,
                                          style: {
                                            fontFamily: "Poppins",
                                            fontSize: 11,
                                            color: "#000000ff",
                                            backgroundColor: "#d2d2d2ff",
                                          },
                                        }}
                                      />
                                    </Grid>
                                  </Grid>
                                </FormControl>
                              </Box>
                            </Grid>

                            <Grid size={{ xs: 12, md: 12, lg: 4, xl: 3 }}>
                              <FormControl
                                component="fieldset"
                                variant="outlined"
                                fullWidth
                                sx={{
                                  height: "100%",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  px: 2,
                                  pt: 2,
                                  pb: 1.5,
                                  position: "relative",
                                  display: "flex",
                                }}
                              >
                                <FormLabel
                                  component="legend"
                                  sx={{
                                    position: "absolute",
                                    top: -10,
                                    left: 12,
                                    px: 1,
                                    bgcolor: "background.paper",
                                    fontSize: 11,
                                    color: "text.secondary",
                                  }}
                                >
                                  Pagamento
                                </FormLabel>

                                <Box
                                  sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  <TextField
                                    sx={textFieldStyles}
                                    fullWidth
                                    value={props.data}
                                    type="date"
                                    onChange={(ev) => {
                                      const val = ev.target.value;
                                      const [ano, mes, dia] = val.split("-");
                                      if (ano && ano.length > 4) {
                                        props.setData(
                                          `${ano.slice(0, 4)}-${
                                            mes || ""
                                          }-${dia || ""}`,
                                        );
                                      } else {
                                        props.setData(val);
                                      }
                                    }}
                                    size="small"
                                    color="success"
                                    label="Data"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                      readOnly: true,
                                      style: {
                                        fontFamily: "Poppins",
                                        fontSize: 11,
                                        color: "#000000ff",
                                        backgroundColor: "#d2d2d2ff",
                                      },
                                    }}
                                  />

                                  <TextField
                                    sx={textFieldStyles}
                                    fullWidth
                                    type="month"
                                    color="success"
                                    size="small"
                                    label="Competência"
                                    InputLabelProps={{ shrink: true }}
                                    value={
                                      props.competencia &&
                                      props.competencia.includes("/")
                                        ? (() => {
                                            const [m, y] =
                                              props.competencia.split("/");
                                            return `${y}-${m}`;
                                          })()
                                        : props.competencia
                                    }
                                    onChange={(ev) => {
                                      const ym = ev.target.value;
                                      if (!ym) {
                                        props.setCompetencia("");
                                        return;
                                      }
                                      const [y, m] = ym.split("-");
                                      props.setCompetencia(`${m}/${y}`);
                                    }}
                                  />

                                  <TextField
                                    sx={textFieldStyles}
                                    fullWidth
                                    size="small"
                                    value={props.valorPagamento}
                                    onChange={(e) =>
                                      props.setValorPagamento(e.target.value)
                                    }
                                    color="success"
                                    label="Valor"
                                    InputProps={{
                                      readOnly: true,
                                      style: {
                                        fontFamily: "Poppins",
                                        fontSize: 11,
                                        color: "#000000ff",
                                        backgroundColor: "#d2d2d2ff",
                                      },
                                    }}
                                  />
                                </Box>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid
                  size={{ xs: 12, md: 4, lg: 3, xl: 3 }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100%",
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      borderRadius: 1,
                      overflow: "hidden",
                      flex: 1,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 0.5,
                        bgcolor: "#cee6ceff",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      Valores
                    </Box>

                    <Box sx={{ p: 1.5, flex: 1 }}>
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            value={`Situação: ${(props.situacao || "").toUpperCase()}`}
                            size="medium"
                            InputProps={{
                              readOnly: true,
                              style: {
                                fontFamily: "Poppins",
                                textAlign: "center",
                                borderRadius: "15px",
                                height: "40px",
                                fontWeight: 800,
                                color: "#000",
                                backgroundColor: getBgBySituacao(),
                              },
                            }}
                          />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            color="success"
                            label="Valor"
                            InputProps={
                              ["1", "4"].includes(String(props.situacaoId))
                                ? {
                                    readOnly: true,
                                    style: {
                                      fontFamily: "Poppins",
                                      fontSize: 11,
                                      color: "#000000ff",
                                      backgroundColor: "#d2d2d2ff",
                                    },
                                  }
                                : {}
                            }
                            disabled={["1", "4"].includes(
                              String(props.situacaoId),
                            )}
                            size="small"
                            required
                            value={props.valorValores}
                            onChange={(e) =>
                              props.setValorValores(e.target.value)
                            }
                            onBlur={(e) => {
                              const vFmt = formatValor(e.target.value, true);
                              props.setValorValores(vFmt);
                              recalcValorCorrigido({ valorValores: vFmt });
                            }}
                          />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            color="success"
                            label="Vr. juros/dia"
                            size="small"
                            InputProps={
                              ["1", "4"].includes(String(props.situacaoId))
                                ? {
                                    readOnly: true,
                                    style: {
                                      fontFamily: "Poppins",
                                      fontSize: 11,
                                      color: "#000000ff",
                                      backgroundColor: "#d2d2d2ff",
                                    },
                                  }
                                : {}
                            }
                            disabled={["1", "4"].includes(
                              String(props.situacaoId),
                            )}
                            value={props.valorJurosDia}
                            onChange={(e) =>
                              props.setValorJurosDia(e.target.value)
                            }
                            onBlur={(e) => {
                              const vFmt = formatValor(e.target.value, true);
                              props.setValorJurosDia(vFmt);
                              recalcValorCorrigido({ valorJurosDia: vFmt });
                            }}
                          />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            InputProps={
                              ["1", "4"].includes(String(props.situacaoId))
                                ? {
                                    readOnly: true,
                                    style: {
                                      fontFamily: "Poppins",
                                      fontSize: 11,
                                      color: "#000000ff",
                                      backgroundColor: "#d2d2d2ff",
                                    },
                                  }
                                : {}
                            }
                            disabled={["1", "4"].includes(
                              String(props.situacaoId),
                            )}
                            color="success"
                            label="Multa (%)"
                            size="small"
                            value={props.multa}
                            onChange={(e) => props.setMulta(e.target.value)}
                            onBlur={(e) => {
                              const vFmt = formatMulta(e.target.value, true); // o seu formatter
                              props.setMulta(vFmt);
                              recalcValorCorrigido({ multa: vFmt });
                            }}
                          />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            color="success"
                            label="Acerto"
                            size="small"
                            InputProps={
                              ["1", "4"].includes(String(props.situacaoId))
                                ? {
                                    readOnly: true,
                                    style: {
                                      fontFamily: "Poppins",
                                      fontSize: 11,
                                      color: "#000000ff",
                                      backgroundColor: "#d2d2d2ff",
                                    },
                                  }
                                : {}
                            }
                            disabled={["1", "4"].includes(
                              String(props.situacaoId),
                            )}
                            value={props.acerto}
                            onChange={(e) => {
                              const v = sanitizeSignedInput(e.target.value);
                              props.setAcerto(v);
                            }}
                            onBlur={(e) => {
                              const vFmt = formatSignedBR(e.target.value);
                              props.setAcerto(vFmt);
                              recalcValorCorrigido({ acerto: vFmt });
                            }}
                          />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                          <TextField
                            sx={textFieldStyles}
                            fullWidth
                            color="success"
                            label="Vr. Corrigido"
                            size="small"
                            value={props.valorCorrigido}
                            onChange={(e) =>
                              props.setValorCorrigido(
                                formatValor(e.target.value, true),
                              )
                            }
                            InputProps={{
                              readOnly: true,
                              style: {
                                fontFamily: "Poppins",
                                fontSize: 11,
                                color: "#000000ff",
                                backgroundColor: "#d2d2d2ff",
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        alignItems: "stretch",
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="inherit"
                        fullWidth
                        sx={{ fontSize: 12 }}
                        onClick={() => props.handleBack()}
                      >
                        Voltar
                      </Button>

                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        onClick={() => props.cadLancamento()}
                        sx={{
                          fontSize: 12,
                          backgroundColor: "#669966",
                        }}
                      >
                        Salvar
                      </Button>

                      {(props.situacaoId === "2" ||
                        props.situacaoId === "3") && (
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          onClick={() => props.handleLiquidar()}
                          sx={{ fontSize: 12, backgroundColor: "#1976d2" }}
                        >
                          Liquidar
                        </Button>
                      )}

                      {props.isEditing && props.isLiquidado && (
                        <Button
                          variant="outlined"
                          color="error"
                          fullWidth
                          onClick={() => props.handleEstornar()}
                          sx={{ fontSize: 12 }}
                        >
                          Estornar
                        </Button>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    </>
  );
};

const styles = StyleSheet.create({
  input: {
    fontFamily: "Poppins !important",
    fontSize: 12,
    fontWeight: 400,
    color: "#808080",
    transitionDuration: "0.2s",
  },
  btn: {
    backgroundColor: "#669966",
    color: "#fff",
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 600,
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "#669966",
    borderRadius: 6,
    display: "flex !important",
    justifyContent: "center !important",
    alignContent: "center !important",
    padding: 0,
    width: "100%",
    height: 30,
    transitionDuration: "0.2s",
    ":hover": {
      color: "#669966",
      backgroundColor: "transparent !important",
      borderColor: "#669966 !important",
    },
    ":active": {
      color: "#669966",
      backgroundColor: "transparent !important",
      borderColor: "#669966 !important",
      opacity: 0.6,
    },
  },
  btnDisabled: {
    backgroundColor: "#B3B3B3",
    color: "#fff",
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 600,
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "#B3B3B3",
    borderRadius: 6,

    display: "flex !important",
    justifyContent: "center !important",
    alignContent: "center !important",
    padding: 0,
    width: "100%",
    height: 34,
  },
  erro: {
    // fontFamily: "Poppins",
    fontSize: 14,
    color: "red",
    textAlign: "center",
    fontWeight: 600,
    marginTop: "1rem",
    marginBottom: "-0.5rem",
  },
});
